import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of, throwError, timer } from 'rxjs';
import { catchError, map, switchMap, retry, timeout, mergeMap, take, debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { environment } from '../../environments/environment';

export interface AddressSuggestion {
  display_name: string;
  lat: number;
  lon: number;
  address: any;
  formatted_address: string;
  place_id?: string;
}

@Injectable({
  providedIn: 'root'
})
export class GeolocationService {
  private addressCache = new Map<string, string>();
  private positionCache: GeolocationPosition | null = null;
  private lastPositionTime: number = 0;
  private isPreloading: boolean = false;
  private preloadPromise: Promise<GeolocationPosition> | null = null;
  private currentPositionReady: boolean = false;

  // Configurazioni ottimizzate per massima precisione GPS
  private readonly GEOLOCATION_OPTIONS = {
    // Configurazione ultra-precisa GPS (tempo maggiore per GPS satellitare)
    ultraPrecise: {
      enableHighAccuracy: true,
      timeout: 20000, // 20 secondi per GPS satellitare
      maximumAge: 0 // Non accettare posizioni cache
    },
    // Configurazione ad alta precisione GPS
    highAccuracy: {
      enableHighAccuracy: true,
      timeout: 15000, // 15 secondi per GPS
      maximumAge: 0 // No cache per massima precisione
    },
    // Configurazione veloce ma comunque precisa
    quick: {
      enableHighAccuracy: true, // Sempre alta precisione
      timeout: 10000, // 10 secondi
      maximumAge: 0 // No cache
    }
  };

  constructor(private http: HttpClient) {
    // precaricamento automatico
  }

  private isLocalDebugEnabled(): boolean {
    if (environment.production) return false;
    const w = globalThis as any;
    const isTest = !!(w?.__karma__ || w?.jasmine);
    const hostname = typeof location !== 'undefined' ? location.hostname : '';
    const isLocalhost = hostname === 'localhost' || hostname === '127.0.0.1' || hostname === '[::1]';
    return isLocalhost && !isTest;
  }

  private debugLog(...args: any[]): void {
    if (this.isLocalDebugEnabled()) console.log(...args);
  }

  private debugWarn(...args: any[]): void {
    if (this.isLocalDebugEnabled()) console.warn(...args);
  }

  private debugError(...args: any[]): void {
    if (this.isLocalDebugEnabled()) console.error(...args);
  }

  /**
   * Cerca indirizzi con civici specifici per una via
   */
  searchAddresses(query: string): Observable<AddressSuggestion[]> {
    if (!query || query.length < 3) {
      return of([]);
    }

    // Filtra query troppo generiche e fornisce suggerimenti
    const genericTerms = ['via', 'viale', 'corso', 'piazza', 'largo', 'strada', 'vicolo'];
    const cleanQuery = query.toLowerCase().trim();
    if (genericTerms.includes(cleanQuery)) {
      return of(this.getGenericSuggestions(cleanQuery));
    }

    // Riduce delay per migliore UX
    const delayMs = 1500; // limit imposto da openstreat map

    // Ottimizza la query 
    const optimizedQuery = this.optimizeSearchQuery(query);

    const url = `https://nominatim.openstreetmap.org/search?` +
      `q=${encodeURIComponent(optimizedQuery)}&` +
      `format=json&` +
      `addressdetails=1&` +
      `limit=10&` +
      `countrycodes=it&` +
      `accept-language=it-IT,it&` +
      `bounded=0&` +
      `dedupe=1&` +
      `featuretype=settlement,street,highway`;

    return timer(delayMs).pipe(
      switchMap(() => this.http.get<any[]>(url)),
      map((results: any[]) => {
        const suggestions = results.map(result => ({
          display_name: result.display_name,
          lat: parseFloat(result.lat),
          lon: parseFloat(result.lon),
          address: result.address || {},
          formatted_address: this.formatSearchAddress(result),
          place_id: result.place_id
        } as AddressSuggestion));
        // Rimuovi duplicati basati su formatted_address
        return this.removeDuplicateAddresses(suggestions);
      }),
      catchError(error => {
        this.debugError('Error fetching addresses from Nominatim:', error);
        return of([]);
      })
    );
  }

  private removeDuplicateAddresses(suggestions: AddressSuggestion[]): AddressSuggestion[] {
    const seen = new Set<string>();
    return suggestions.filter(suggestion => {
      const key = suggestion.formatted_address.toLowerCase().trim();
      if (seen.has(key)) {
        return false;
      }
      seen.add(key);
      return true;
    });
  }

  private getGenericSuggestions(term: string): AddressSuggestion[] {
    const suggestions = {
      'via': [
        { display_name: 'Via Roma', formatted_address: 'Via Roma', lat: 41.9028, lon: 12.4964 },
        { display_name: 'Via Milano', formatted_address: 'Via Milano', lat: 45.4642, lon: 9.1900 },
        { display_name: 'Via Napoli', formatted_address: 'Via Napoli', lat: 40.8522, lon: 14.2681 }
      ],
      'viale': [
        { display_name: 'Viale Europa', formatted_address: 'Viale Europa', lat: 41.9028, lon: 12.4964 },
        { display_name: 'Viale Italia', formatted_address: 'Viale Italia', lat: 41.9028, lon: 12.4964 }
      ],
      'corso': [
        { display_name: 'Corso Italia', formatted_address: 'Corso Italia', lat: 41.9028, lon: 12.4964 },
        { display_name: 'Corso Roma', formatted_address: 'Corso Roma', lat: 41.9028, lon: 12.4964 }
      ]
    };

    return (suggestions as any)[term] || [];
  }

  private optimizeSearchQuery(query: string): string {
    let optimized = query.trim();

    // Se inizia solo con un tipo di strada, aggiungi Roma come fallback
    const streetTypes = /^(via|viale|corso|piazza|largo|strada|vicolo)\s+(.+)$/i;
    const match = optimized.match(streetTypes);

    if (match && match[2].length < 4) {
      // Se dopo il tipo strada c'è poco testo, aggiungi Roma
      optimized = `${optimized}, Roma`;
    }

    // Migliora query incomplete
    if (optimized.length < 8 && !optimized.includes(',')) {
      optimized = `${optimized}, Italia`;
    }

    return optimized;
  }

  private formatSearchAddress(result: any): string {
    const address = result.address || {};
    const parts = [];

    // Costruisci l'indirizzo formattato
    if (address.road || address.street) {
      parts.push(`${address.road || address.street}${address.house_number ? ' ' + address.house_number : ''}`);
    }
    if (address.city || address.town || address.village) {
      parts.push(address.city || address.town || address.village);
    }
    if (address.state || address.region) {
      parts.push(`(${address.state || address.region})`);
    }

    return parts.length > 0 ? parts.join(', ') : result.display_name;
  }

  /**
   * Seleziona un indirizzo dai suggerimenti e ottiene coordinate precise
   */
  selectAddress(suggestion: AddressSuggestion): Observable<{ lat: number, lng: number, address: string }> {
    // Nota: convertiamo 'lon' in 'lng' nell'output per mantenere la compatibilità
    return of({
      lat: suggestion.lat,
      lng: suggestion.lon, // Converti da lon a lng qui
      address: suggestion.formatted_address
    });
  }

  /**
   * Metodo avanzato che prova multiple strategie per ottenere la posizione più accurata
   */
  getCurrentPositionAdvanced(): Observable<GeolocationPosition> {
    return new Observable(observer => {
      if (!navigator.geolocation) {
        observer.error('Geolocation non supportata dal browser');
        return;
      }

      let bestPosition: GeolocationPosition | null = null;
      let attempts = 0;
      const maxAttempts = 3;

      const tryNextStrategy = () => {
        attempts++;
        const options = attempts === 1 ? this.GEOLOCATION_OPTIONS.ultraPrecise :
          attempts === 2 ? this.GEOLOCATION_OPTIONS.highAccuracy :
            this.GEOLOCATION_OPTIONS.quick;

        navigator.geolocation.getCurrentPosition(
          (position) => {
            const accuracy = position.coords.accuracy;

            // Se è la prima posizione o è significativamente più precisa
            if (!bestPosition || accuracy < bestPosition.coords.accuracy * 0.8) {
              bestPosition = position;
            }

            // Se abbiamo una buona accuratezza (< 50m) o abbiamo finito i tentativi
            if (accuracy < 50 || attempts >= maxAttempts) {
              observer.next(bestPosition!);
              observer.complete();
            } else {
              // Prova il prossimo metodo
              setTimeout(tryNextStrategy, 1000);
            }
          },
          (error) => {
            this.debugWarn(`Tentativo ${attempts} fallito:`, this.getGeolocationErrorMessage(error));

            if (attempts >= maxAttempts) {
              if (bestPosition) {
                //console.log('Uso la migliore posizione ottenuta');
                observer.next(bestPosition);
                observer.complete();
              } else {
                observer.error(this.getGeolocationErrorMessage(error));
              }
            } else {
              // Prova il prossimo metodo
              setTimeout(tryNextStrategy, 1000);
            }
          },
          options
        );
      };

      tryNextStrategy();
    });
  }

  /**
   * Metodo per ottenere posizione con watchPosition (più accurato nel tempo)
   */
  watchPositionForAccuracy(targetAccuracy: number = 20, maxWaitTime: number = 60000): Observable<GeolocationPosition> {
    return new Observable(observer => {
      if (!navigator.geolocation) {
        observer.error('Geolocation non supportata dal browser');
        return;
      }

      let bestPosition: GeolocationPosition | null = null;
      let watchId: number;
      let timeoutId: any;

      watchId = navigator.geolocation.watchPosition(
        (position) => {
          const accuracy = position.coords.accuracy;

          if (!bestPosition || accuracy < bestPosition.coords.accuracy) {
            bestPosition = position;
          }

          // Se raggiungiamo l'accuratezza target, completa subito
          if (accuracy <= targetAccuracy) {
            navigator.geolocation.clearWatch(watchId);
            clearTimeout(timeoutId);
            observer.next(bestPosition);
            observer.complete();
          }
        },
        (error) => {
          this.debugError('Errore nel monitoraggio:', error);
          navigator.geolocation.clearWatch(watchId);
          clearTimeout(timeoutId);

          if (bestPosition) {
            observer.next(bestPosition);
            observer.complete();
          } else {
            observer.error(this.getGeolocationErrorMessage(error));
          }
        },
        this.GEOLOCATION_OPTIONS.ultraPrecise
      );

      // Timeout: usa la migliore posizione ottenuta finora
      timeoutId = setTimeout(() => {
        navigator.geolocation.clearWatch(watchId);

        if (bestPosition) {
          observer.next(bestPosition);
          observer.complete();
        } else {
          observer.error('Timeout: nessuna posizione ottenuta');
        }
      }, maxWaitTime);
    });
  }

  /**
   * Precarica la posizione corrente con massima precisione GPS
   */
  private preloadCurrentPosition(): void {
    if (this.isPreloading || !navigator.geolocation) {
      return;
    }

    this.isPreloading = true;

    this.preloadPromise = new Promise((resolve, reject) => {
      // Prima richiesta per attivare il GPS
      navigator.geolocation.getCurrentPosition(
        (position) => {
 
          // Se già abbastanza precisa (< 20m), usala
          if (position.coords.accuracy <= 20) {
            this.positionCache = position;
            this.lastPositionTime = Date.now();
            this.currentPositionReady = true;
            this.isPreloading = false;
            resolve(position);
            return;
          }

          // Altrimenti aspetta che il GPS si stabilizzi
          let watchId: number;
          let bestPosition = position;
          let attempts = 0;
          const maxAttempts = 10;

          watchId = navigator.geolocation.watchPosition(
            (newPosition) => {
              attempts++;

              if (newPosition.coords.accuracy < bestPosition.coords.accuracy) {
                bestPosition = newPosition;
              }

              // Se raggiungiamo buona precisione O max tentativi
              if (newPosition.coords.accuracy <= 15 || attempts >= maxAttempts) {
                navigator.geolocation.clearWatch(watchId);
                this.positionCache = bestPosition;
                this.lastPositionTime = Date.now();
                this.currentPositionReady = true;
                this.isPreloading = false;
                resolve(bestPosition);
              }
            },
            (watchError) => {
              navigator.geolocation.clearWatch(watchId);
              // Usa la migliore posizione ottenuta
              this.positionCache = bestPosition;
              this.lastPositionTime = Date.now();
              this.currentPositionReady = true;
              this.isPreloading = false;
              resolve(bestPosition);
            },
            this.GEOLOCATION_OPTIONS.ultraPrecise
          );

          // Timeout di sicurezza per il watch
          setTimeout(() => {
            navigator.geolocation.clearWatch(watchId);
            if (this.isPreloading) {
              this.positionCache = bestPosition;
              this.lastPositionTime = Date.now();
              this.currentPositionReady = true;
              this.isPreloading = false;
              resolve(bestPosition);
            }
          }, 45000);
        },
        (error) => {
          this.debugWarn('Precaricamento GPS fallito:', this.getGeolocationErrorMessage(error));
          this.isPreloading = false;
          reject(error);
        },
        this.GEOLOCATION_OPTIONS.highAccuracy
      );
    });
  }

  /**
   * Restituisce true se la posizione è già disponibile in cache
   */
  isPositionReady(): boolean {
    const now = Date.now();
    return this.positionCache !== null &&
      this.currentPositionReady &&
      (now - this.lastPositionTime) < 60000; // 1 minuto di validità
  }

  /**
   * Ottiene la posizione immediatamente se disponibile, altrimenti la richiede
   */
  getCurrentPositionFast(): Observable<GeolocationPosition> {
    // Blocca il GPS se l'utente ha selezionato manualmente
    if ((window as any).manualLocationSelected) {
      this.debugLog('GPS globale bloccato: posizione selezionata manualmente');
      return throwError('GPS bloccato: posizione selezionata manualmente');
    }

    const now = Date.now();

    // Se abbiamo una posizione recente, restituiscila subito
    if (this.positionCache && (now - this.lastPositionTime) < 60000) {
      return of(this.positionCache);
    }

    // Se il precaricamento è in corso, aspettalo
    if (this.preloadPromise && this.isPreloading) {
      return new Observable(observer => {
        this.preloadPromise!.then(
          position => {
            // Doppio controllo prima di restituire la posizione
            if ((window as any).manualLocationSelected) {
              this.debugLog('GPS bloccato durante precaricamento');
              observer.error('GPS bloccato: posizione selezionata manualmente');
              return;
            }
            observer.next(position);
            observer.complete();
          },
          error => {
            // Se il precaricamento fallisce, prova il metodo normale
            this.getCurrentPosition(false).subscribe(observer);
          }
        );
      });
    }

    // Altrimenti usa il metodo normale veloce
    return this.getCurrentPosition(false);
  }

  /**
   * Geolocalizzazione veloce: one-shot, timeout breve, accetta cache, enableHighAccuracy false.
   * Ideale per UX reattiva; la posizione si può poi spostare sulla mappa se serve.
   */
  getCurrentPositionQuick(): Observable<GeolocationPosition> {
    if ((window as any).manualLocationSelected) {
      return throwError('GPS bloccato: posizione selezionata manualmente');
    }

    const now = Date.now();
    if (this.positionCache && (now - this.lastPositionTime) < 120000) {
      return of(this.positionCache);
    }

    return new Observable(observer => {
      if (!navigator.geolocation) {
        observer.error('Geolocation non supportata');
        return;
      }

      const options: PositionOptions = {
        enableHighAccuracy: false,
        timeout: 6000,
        maximumAge: 15000
      };

      navigator.geolocation.getCurrentPosition(
        (position) => {
          this.positionCache = position;
          this.lastPositionTime = Date.now();
          observer.next(position);
          observer.complete();
        },
        (err) => observer.error(this.getGeolocationErrorMessage(err)),
        options
      );
    });
  }

  /**
   * Metodo principale migliorato che combina le strategie
   */
  getCurrentPosition(useAdvanced: boolean = false): Observable<GeolocationPosition> {
    // Blocca il GPS se l'utente ha selezionato manualmente
    if ((window as any).manualLocationSelected) {
      this.debugLog('GPS normale bloccato: posizione selezionata manualmente');
      return throwError('GPS bloccato: posizione selezionata manualmente');
    }

    const now = Date.now();
    if (this.positionCache && (now - this.lastPositionTime) < 300000) {
      this.debugLog('Uso posizione da cache');
      return of(this.positionCache);
    }

    return new Observable(observer => {
      let bestPosition: GeolocationPosition | null = null;
      let watchId: number;
      let completed = false;
      let readings = 0;

      //console.log('Inizio acquisizione GPS precisa...');

      watchId = navigator.geolocation.watchPosition(
        (position) => {
          if (completed) return;
          
          // Controllo durante l'acquisizione
          if ((window as any).manualLocationSelected) {
            navigator.geolocation.clearWatch(watchId);
            completed = true;
            //console.log('GPS interrotto: posizione selezionata manualmente');
            observer.error('GPS bloccato: posizione selezionata manualmente');
            return;
          }
          
          readings++;
          
          const accuracy = position.coords.accuracy;

          if (!bestPosition || accuracy < bestPosition.coords.accuracy) {
            bestPosition = position;
          }

          // Completa se: precisione < 20m O 5 letture O precisione < 50m dopo 3 letture
          if (accuracy <= 20 || readings >= 5 || (accuracy <= 50 && readings >= 3)) {
            navigator.geolocation.clearWatch(watchId);
            completed = true;
            
            this.positionCache = bestPosition!;
            this.lastPositionTime = now;
            this.currentPositionReady = true;
            
            observer.next(bestPosition!);
            observer.complete();
          }
        },
        (error) => {
          if (completed) return;
          navigator.geolocation.clearWatch(watchId);
          completed = true;
          
          if (bestPosition) {
            this.positionCache = bestPosition;
            this.lastPositionTime = now;
            observer.next(bestPosition);
            observer.complete();
          } else {
            observer.error(this.getGeolocationErrorMessage(error));
          }
        },
        {
          enableHighAccuracy: true,
          timeout: 60000,
          maximumAge: 0
        }
      );

      // Timeout di sicurezza
      setTimeout(() => {
        if (!completed) {
          navigator.geolocation.clearWatch(watchId);
          completed = true;
          
          if (bestPosition) {
            this.positionCache = bestPosition;
            this.lastPositionTime = now;
            observer.next(bestPosition);
            observer.complete();
          } else {
            observer.error('Timeout GPS');
          }
        }
      }, 30000);
    });
  }

  /**
   * Versione ottimizzata per GPS ad alta precisione
   */
  private getCurrentPositionOptimized(): Observable<GeolocationPosition> {
    return new Observable(observer => {
      if (!navigator.geolocation) {
        observer.error('Geolocation non supportata dal browser');
        return;
      }

      // Usa watchPosition per aspettare che il GPS si stabilizzi
      let watchId: number;
      let bestPosition: GeolocationPosition | null = null;
      let completed = false;
      let readings = 0;
      const maxReadings = 8;

      watchId = navigator.geolocation.watchPosition(
        (position) => {
          if (completed) return;
          readings++;

          const accuracy = position.coords.accuracy;


          // Tieni la posizione più precisa
          if (!bestPosition || accuracy < bestPosition.coords.accuracy) {
            bestPosition = position;
          }

          // Completa se:
          // 1. Precisione ottima (< 15m)
          // 2. Precisione buona (< 30m) dopo 3 letture
          // 3. Max letture raggiunte
          if (accuracy <= 15 ||
            (accuracy <= 30 && readings >= 3) ||
            readings >= maxReadings) {

            navigator.geolocation.clearWatch(watchId);
            completed = true;

            observer.next(bestPosition!);
            observer.complete();
          }
        },
        (error) => {
          if (completed) return;
          navigator.geolocation.clearWatch(watchId);
          completed = true;

          if (bestPosition) {
            observer.next(bestPosition);
            observer.complete();
          } else {
            observer.error(this.getGeolocationErrorMessage(error));
          }
        },
        this.GEOLOCATION_OPTIONS.ultraPrecise
      );

      // Timeout di sicurezza - 30 secondi
      setTimeout(() => {
        if (!completed) {
          navigator.geolocation.clearWatch(watchId);
          completed = true;
          
          if (bestPosition) {
            observer.next(bestPosition);
            observer.complete();
          } else {
            observer.error('Timeout: nessuna posizione GPS ottenuta');
          }
        }
      }, 30000);
    });
  }

  /**
   * Geocoding migliorato con multiple API italiane
   */
  getDetailedAddress(lat: number, lng: number): Observable<any> {

    // Prova prima con API italiana specifica per maggiore accuratezza locale
    return this.getItalianGeocodingAPI(lat, lng).pipe(
      catchError(error => {
        this.debugWarn('🇮🇹 API italiana fallita, provo Nominatim:', error);
        return this.getNominatimOptimized(lat, lng);
      }),
      catchError(error => {
        this.debugWarn('Nominatim fallito, provo BigDataCloud:', error);
        return this.getBigDataCloudAddress(lat, lng);
      }),
      retry(1),
      timeout(3000)
    );
  }

  /**
   * API italiana ottimizzata per massimo dettaglio indirizzi
   */
  private getItalianGeocodingAPI(lat: number, lng: number): Observable<any> {
    // Nominatim con parametri ottimizzati per Italia e numeri civici
    const url = `https://nominatim.openstreetmap.org/reverse?` +
      `format=json&lat=${lat}&lon=${lng}&` +
      `addressdetails=1&zoom=19&` + // Zoom 19 per massimo dettaglio
      `accept-language=it-IT,it&` +
      `countrycodes=it&` +
      `extratags=1&namedetails=1&` +
      `layer=address&` + // Forza layer indirizzo
      `dedupe=0`; // Non de-duplicare per avere tutti i dettagli

    return this.http.get(url).pipe(
      map((response: any) => {
        if (response.error || !response.address) {
          throw new Error('Risposta non valida da Nominatim IT');
        }
        // Se non abbiamo il civico, prova a estrarlo da altri campi
        if (!response.address.house_number && response.display_name) {
          const civico = this.extractHouseNumber(response.display_name);
          if (civico) {
            response.address.house_number = civico;
          }
        }
        return response.address;
      })
    );
  }

  /**
   * Estrae il numero civico da una stringa di indirizzo
   */
  private extractHouseNumber(text: string): string | null {
    // Pattern per numero civico italiano (es: "12", "12A", "12/A", "12 bis")
    const patterns = [
      /\b(\d+(?:[A-Za-z]|\/[A-Za-z]|\s+bis|\s+ter)?)\b/g,
      /(?:n\.|numero|num\.?)\s*(\d+(?:[A-Za-z]|\/[A-Za-z])?)/gi,
      /(?:^|,)\s*(\d+(?:[A-Za-z]|\/[A-Za-z])?)\s*(?:,|$)/g
    ];

    for (const pattern of patterns) {
      const match = text.match(pattern);
      if (match && match[0]) {
        // Estrai solo la parte numerica + eventuale lettera
        const cleanNumber = match[0].trim().replace(/^n\.?|numero|num\.?/gi, '').trim();
        if (cleanNumber.match(/^\d+[A-Za-z\/]?/)) {
          //console.log(` Civico trovato con pattern: "${cleanNumber}" da "${text}"`);
          return cleanNumber;
        }
      }
    }

    return null;
  }

  /**
   * Nominatim ottimizzato con rate limiting
   */
  private getNominatimOptimized(lat: number, lng: number): Observable<any> {
    // Aggiungo un piccolo delay per rispettare il rate limiting di Nominatim
    return timer(500).pipe(
      switchMap(() => {
        const url = `https://nominatim.openstreetmap.org/reverse?` +
          `format=json&lat=${lat}&lon=${lng}&` +
          `addressdetails=1&zoom=18&` +
          `accept-language=it-IT,it,en`;

        return this.http.get(url);
      }),
      map((response: any) => {
        if (response.error || !response.address) {
          throw new Error('Risposta non valida da Nominatim');
        }
        //console.log('Risposta Nominatim:', response.address);
        return response.address;
      })
    );
  }

  private getBigDataCloudAddress(lat: number, lng: number): Observable<any> {
    const url = `https://api.bigdatacloud.net/data/reverse-geocode-client?` +
      `latitude=${lat}&longitude=${lng}&localityLanguage=it`;

    return this.http.get(url).pipe(
      map((data: any) => {
        const address: any = {};

        if (data.locality) address.city = data.locality;
        if (data.principalSubdivision) address.state = data.principalSubdivision;
        if (data.countryName) address.country = data.countryName;

        return address;
      })
    );
  }


  private getGeolocationErrorMessage(error: GeolocationPositionError): string {
    switch (error.code) {
      case error.PERMISSION_DENIED:
        return 'Permesso negato. Abilita la geolocalizzazione nelle impostazioni del browser.';
      case error.POSITION_UNAVAILABLE:
        return 'Posizione non disponibile. Controlla GPS/WiFi e riprova.';
      case error.TIMEOUT:
        return 'Timeout. Il GPS potrebbe richiedere più tempo. Riprova.';
      default:
        return `Errore di geolocalizzazione: ${error.message}`;
    }
  }

  getAddress(lat: number, lng: number): Observable<string> {
    const cacheKey = `${lat.toFixed(6)},${lng.toFixed(6)}`;

    if (this.addressCache.has(cacheKey)) {
      return of(this.addressCache.get(cacheKey)!);
    }

    return this.getDetailedAddress(lat, lng).pipe(
      map(address => {
        const formattedAddress = this.formatAddress(address);
        this.addressCache.set(cacheKey, formattedAddress);
        return formattedAddress;
      }),
      catchError(error => {
        this.debugError('Errore nella geocodifica:', error);
        return of('');
      })
    );
  }

  private formatAddress(address: any): string {
    const parts = [];
    let streetPart = '';
    const streetName = address.road || address.street || address.highway ||
      address.pedestrian || address.footway || address.path ||
      address.residential || address.service;

    if (streetName) {
      streetPart = streetName;

      const houseNumber = address.house_number || address.addr_housenumber ||
        address.building_number || address.number;

      if (houseNumber) {
        streetPart += ` ${houseNumber}`;
      } 

      parts.push(streetPart);
    } else {
      this.debugWarn('Nessuna via trovata nei dati GPS');
    }

    const locality = address.city || address.town || address.village ||
      address.hamlet || address.municipality || address.suburb ||
      address.neighbourhood || address.quarter || address.district;

    if (locality) {
      parts.push(locality);
    }

    const region = address.county || address.state || address.region || address.province;
    if (region && !parts.some(p => p.toLowerCase().includes(region.toLowerCase()))) {
      parts.push(`(${region})`);
    }

    if (address.postcode) {
      const postcodeStr = address.postcode.toString();
      const addressStr = parts.join(', ');
      
      if (!addressStr.includes(postcodeStr)) {
        const lastPart = parts[parts.length - 1];
        if (lastPart && !lastPart.includes('(')) {
          parts[parts.length - 1] = `${lastPart} ${postcodeStr}`;
        } else {
          parts.push(postcodeStr);
        }
      }
    }

    const result = parts.length > 0 ? parts.join(', ') : 'Indirizzo non disponibile';

    if (!streetName && (address.display_name || address.formatted_address)) {
      const displayName = address.display_name || address.formatted_address;
      const firstPart = displayName.split(',')[0].trim();

      if (firstPart.match(/\d+/) ||
        firstPart.match(/\b(via|viale|corso|piazza|largo|strada|vicolo)\b/i)) {
        return `${firstPart}, ${result}`;
      }
    }

    return result;
  }
}